// src/components/WebsitePage1.js
import React, { useEffect, useState, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Container, Button, Alert, Spinner, Card, Form, Row, Col, Collapse, ProgressBar, Badge } from 'react-bootstrap';
import useToken from '../../hooks/useToken';
import useProgressRedirect from '../../hooks/useProgressRedirect'; // 🆕 Import useProgressRedirect
import ProgressTracker from '../shared/ProgressTracker'; // 🆕 Import ProgressTracker
import { EmbedPDF } from '@simplepdf/react-embed-pdf'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faFilePdf, faProjectDiagram } from '@fortawesome/free-solid-svg-icons';

/**
 * 
 * @param {object} props
 * @param {number} props.order_index 1, 2
 * @param {string} props.next_navigation /website2, /post-experiment
 * @param {string} props.route_name WEBSITE1, WEBSITE2
 * @param {string} props.next_route_name WEBSITE1, POST_EXPERIMENT
 * @returns 
 */
function WebsitePage({ order_index, route_name, next_route_name, next_navigation }) {
  const { id } = useParams();
  const navigate = useNavigate();
  const { token, refreshAccessToken } = useToken();

  const [website, setWebsite] = useState(null);
  const [dfdUrl, setDfdUrl] = useState(null);
  const [designDocUrl, setDesignDocUrl] = useState(null);
  const [serverIP, setServerIP] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [vulnForms, setVulnForms] = useState([]);
  const [vulnTypes, setVulnTypes] = useState([]);
  const [completing, setCompleting] = useState(false);
  const [savingAll, setSavingAll] = useState(false);

  const timerRef = useRef(null);
  const [timeLeft, setTimeLeft] = useState(null);
  const [startAdditionalTime, setStartAdditionalTime] = useState(false);
  const additionalTimeRef = useRef(false);
  const isFirstTime = useRef(true);
  const isStartFirstTime = useRef(true);

  const MAX_IMAGE_BYTES = 5 * 1024 * 1024;   // 5 MB

  const ADDITIONAL_MINS = 30;

  useProgressRedirect(route_name); // <=== Auto redirect if necessary!

  useEffect(() => {
    const init = async () => {
      const authToken = token || await refreshAccessToken();

      try {

        const assignRes = await fetch("/api"+`/assignment`, {
          headers: { Authorization: `Bearer ${authToken}` },
        });
        const assignments = await assignRes.json();

        // Get the website assignment with order_id
        const assignedWebsite = assignments.find(w => w.order_index === order_index || w.order_index === String(order_index));
        if (!assignedWebsite) {
          setError("You are not assigned to this website.");
          return;
        }
        setWebsite(assignedWebsite);
        console.log(assignedWebsite);

        // 🔽 Fetch the design document PDF
        const designDocRes = await fetch("/api"+`/website/${assignedWebsite.website_id}/design-document-pdf`, {
          headers: { Authorization: `Bearer ${authToken}` },
        });

        if (designDocRes.ok) {
          const blob = await designDocRes.blob();
          setDesignDocUrl(URL.createObjectURL(blob));
        }


        // 🔽 Fetch the threat model viewer
        const threatRes = await fetch("/api"+`/website/${assignedWebsite.website_id}/threat-model`, {
          headers: { Authorization: `Bearer ${authToken}` },
        });

        if (threatRes.ok) {
          setDfdUrl("/threatmodelviewer-page1");
        } else {
          console.warn("Threat model not available for this website.");
          setDfdUrl(null); // Set it to null to indicate it's not available
        }

        // Fetch vulnerability types
        const vulnTypeRes = await fetch("/api"+`/vulnerability-types`, {
          headers: { Authorization: `Bearer ${authToken}` },
        });
        const vulnTypeData = await vulnTypeRes.json();
        setVulnTypes(vulnTypeData);

        // Fetch saved findings
        const findingsRes = await fetch("/api"+`/website/${assignedWebsite.website_id}/findings`, {
          headers: { Authorization: `Bearer ${authToken}` },
        });
        if (findingsRes.ok) {
          const savedFindings = await findingsRes.json();
          const formatted = savedFindings.map(f => ({
            ...f,
            vulnerability_type: (() => {
              if (typeof f.vulnerability_type === 'string') {
                try {
                  const parsed = JSON.parse(f.vulnerability_type);
                  return Array.isArray(parsed) ? parsed : [f.vulnerability_type];
                } catch (e) {
                  return f.vulnerability_type ? [f.vulnerability_type] : [];
                }
              }
              return Array.isArray(f.vulnerability_type) ? f.vulnerability_type : [];
            })(),
            isNew: false,
          }));
          setVulnForms(formatted);
        }
      } catch (err) {
        console.error(err);
        setError('Failed to load assignment.');
      }
    };
    init();

    // return () => clearInterval(timerRef.current); // cleanup timer when page unmounts

  }, [id]);

  /* ───────────────────── helpers (auth + upload) ────────────────── */
  const withAuth = async (opts = {}) => {
    const authToken = token || (await refreshAccessToken());
    return {
      ...opts,
      headers: {
        ...(opts.headers || {}),
        Authorization: `Bearer ${authToken}`,
      },
    };
  };

  /** upload file → `/upload/poc-image` → return CDN url */
  const uploadScreenshot = async (file) => {
    const fd = new FormData();
    fd.append("file", file);
    fd.append("website_id", website.website_id);
    const res = await fetch("/api/upload/poc-image", await withAuth({ method: "POST", body: fd }));
    if (!res.ok) throw new Error("Image upload failed");
    const { url } = await res.json();
    return url;
  };

  /** called when user picks a file for one finding */
  const handleFileChange = async (idx, file) => {
    if (!file) return;

    if (file.size > MAX_IMAGE_BYTES) {
      alert("Screenshot must be ≤ 5 MB");
      return;
    }

    setVulnForms(fs => { const c=[...fs]; c[idx].uploading=true; return c; });
    try {
      const url = await uploadScreenshot(file);
      setVulnForms(fs => { const c=[...fs]; c[idx].poc_image_path=url; c[idx].uploading=false; return c; });
    } catch (e) {
      alert(e.message);
      setVulnForms(fs => { const c=[...fs]; c[idx].uploading=false; return c; });
    }
  };

  const handleStartServer = async () => {
    setLoading(true);
    setError('');
    try {
      const res = await fetch("/api"+`/website/${website.website_id}/start`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token || await refreshAccessToken()}` },
      });
      const data = await res.json();
      if (res.ok && data.hostname) {
        setServerIP(data.hostname);
        
        // ✅ Re-fetch the assignment data to get the updated `server_started_at`
        const authToken = token || await refreshAccessToken();

        try {

          const assignRes = await fetch("/api"+`/assignment`, {
            headers: { Authorization: `Bearer ${authToken}` },
          });
          
          if (!assignRes.ok) {
            console.error('Failed to fetch assignment:', assignRes.status);
            return;
          }
          
          const assignments = await assignRes.json();

          // Get the website assignment with order_id 1
          const assignedWebsite = assignments.find(w => w.order_index === order_index);
          if (!assignedWebsite) {
            setError("You are not assigned to this website.");
            return;
          }
          setWebsite(assignedWebsite);
        } catch (err) {
          console.error(err);
          setError('Failed to load assignment.');
        }
      } else {
        setError(data.message || 'Failed to start server.');
      }
    } catch (err) {
      setError('Server startup error.');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (!serverIP && website && isStartFirstTime.current) {
      isStartFirstTime.current = false;
      handleStartServer();
    }
  })

  const handleRestart = async () => {
    setLoading(true);
    setError('');
    try {
      const res = await fetch("/api"+`/website/${website.website_id}/restart`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token || await refreshAccessToken()}` },
      });
      const data = await res.json();
      if (res.ok && data.hostname) {
        setServerIP(data.hostname);
      } else {
        setError(data.message || 'Failed to restart server.');
      }
    } catch (err) {
      setError('Server restart error.');
    } finally {
      setLoading(false);
    }
  };

  const handleStop = async () => {
    if (!website) return;
    setLoading(true);
    setError('');
    try {
      const res = await fetch("/api"+`/website/${website.website_id}/stop`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token || await refreshAccessToken()}` },
      });
      const data = await res.json();
      if (!res.ok) {
        setError(data.message || 'Failed to stop server.');
      } else {
        setServerIP('');
      }
    } catch (err) {
      setError('Server stop error.');
    } finally {
      setLoading(false);
    }
  };


  const handleAddFinding = () => {
    if (vulnForms.length >= 20) return;
    setVulnForms([...vulnForms, { id: null, vulnerability_type: [], name: '', affected_urls: '', description: '', payload: '', poc_image_path: '', used_threat_model: false, threat_model_info: '', used_design_document: false, design_document_info: '', isNew: true, collapsed: false }]);
  };

  const handleRemoveFinding = async (index) => {
    const finding = vulnForms[index];
  
    if (finding.id) {
      // If it has an ID, it's saved in the backend, delete it first
      const confirmDelete = window.confirm("This finding is already saved. Do you really want to delete it?");
      if (!confirmDelete) return;
  
      try {
        const res = await fetch("/api"+`/website/${website.website_id}/finding/${finding.id}`, {
          method: 'DELETE',
          headers: {
            Authorization: `Bearer ${token || await refreshAccessToken()}`,
          },
        });
        if (!res.ok) {
          const data = await res.json();
          alert(data.message || "Failed to delete finding on server.");
          return;
        }
      } catch (err) {
        console.error('Failed to delete finding:', err);
        alert("Error deleting finding.");
        return;
      }
    }
  
    // Remove from UI
    const updated = [...vulnForms];
    updated.splice(index, 1);
    setVulnForms(updated);
  };
  
  const toggleCollapse = (index) => {
    const updated = [...vulnForms];
    updated[index].collapsed = !updated[index].collapsed;
    setVulnForms(updated);
  };

  const handleSaveFinding = async (index) => {
    const finding = vulnForms[index];
    if (!finding.vulnerability_type || finding.vulnerability_type.length === 0 || !finding.name || !finding.affected_urls) return alert("Vulnerability types, name, and affected urls are required.");

    const url = `/website/${website.website_id}/finding${finding.id ? `/${finding.id}` : ''}`;
    const method = finding.id ? 'PUT' : 'POST';

    const payload = {
      ...finding,
      vulnerability_type: JSON.stringify(finding.vulnerability_type)
    };
    const res = await fetch("/api"+url, {
      method,
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${token || await refreshAccessToken()}`,
      },
      body: JSON.stringify(payload),
    });
    const result = await res.json();
    if (res.ok) {
      const updated = [...vulnForms];
      updated[index].isNew = false;
      if (!finding.id && result.finding_id) updated[index].id = result.finding_id;
      setVulnForms(updated);
    } else {
      alert(result.message || 'Failed to save finding');
    }
  };

  const handleFindingChange = (index, field, value) => {
    const updated = [...vulnForms];
    updated[index][field] = value;
    setVulnForms(updated);
  };

  const handleVulnTypeToggle = (findingIndex, vulnType) => {
    const updatedForms = [...vulnForms];
    const finding = updatedForms[findingIndex];
    const currentTypes = finding.vulnerability_type || [];

    if (currentTypes.includes(vulnType)) {
      finding.vulnerability_type = currentTypes.filter(vt => vt !== vulnType);
    } else {
      finding.vulnerability_type = [...currentTypes, vulnType];
    }
    setVulnForms(updatedForms);
  };

  // put this just above the return statement, beside the other handlers
  const saveAllFindings = React.useCallback(async () => {
    if (!website) return;

    // make a *fresh* token only once
    const authToken = token || await refreshAccessToken();

    // Kick off one request per finding (skip blanks)
    const savePromises = vulnForms
      .filter(f => f.vulnerability_type && f.vulnerability_type.length > 0)        // ignore empty rows
      .map(async f => {
        const url = `/website/${website.website_id}/finding${f.id ? `/${f.id}` : ''}`;
        const method = f.id ? 'PUT' : 'POST';
        const payload = {
          ...f,
          vulnerability_type: JSON.stringify(f.vulnerability_type)
        };
        const res = await fetch("/api"+url, {
          method,
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${authToken}`,
          },
          body: JSON.stringify(payload),
        });
        if (!res.ok) {
          // surface one error and let the rest continue
          const data = await res.json();
          throw new Error(data.message || 'Save failed');
        }
        return res.json();     // not strictly needed, but nice if you care
      });

    // Wait for all of them (fail fast on *any* error)
    await Promise.all(savePromises);

    // Mark everything clean in the UI
    setVulnForms(fs => fs.map(f => ({ ...f, isNew: false, collapsed: false })));
  }, [refreshAccessToken, token, vulnForms, website]);

  const handleFinish = async () => {
    setCompleting(true);
    try {
      await saveAllFindings();           // 🆕 bulk save first
      if (serverIP) await handleStop();  // stop the server only after saving

      const authToken = token || await refreshAccessToken();
      await fetch("/api"+'/player/progress', {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${authToken}` },
        body: JSON.stringify({ progress_step: next_route_name }),
      });

      navigate(next_navigation, { replace: true });
      window.location.reload();
    } catch (err) {
      console.error(err);
      alert(err.message ?? 'Failed to complete - some findings may not be saved.');
    } finally {
      setCompleting(false);
    }
  };

  useEffect(() => {
    if (!website) return;
    if (website.server_started_at) {
      if (!isFirstTime.current) return;
      isFirstTime.current = false;
    
      // clearInterval(timerRef.current); // always clear previous timer
      const start = new Date(website.server_started_at);
      timerRef.current = setInterval(() => {
        const now = new Date();
        const elapsed = Math.floor((now - start) / 1000);
        const remaining = Math.max(0, ((website.time_limit_minutes ?? 60) - (!startAdditionalTime && ADDITIONAL_MINS)) * 60 - elapsed);
        setTimeLeft(remaining);
        if (remaining <= 0) {
          if (!additionalTimeRef.current) {
            if (window.confirm("Do you want to complete the test?")) {
              clearInterval(timerRef.current);
              (async () => {
                await handleFinish();
              })();
              return;
            }
            setStartAdditionalTime(true);
            additionalTimeRef.current = true;
          } else {
            clearInterval(timerRef.current);
            (async () => {
              await handleFinish();
            })();
          }
        }
      }, 1000);
    } else {
      // If server not started yet, full time is left
      setTimeLeft(((website.time_limit_minutes ?? 60) - ADDITIONAL_MINS) * 60);
    }
  
    // return () => clearInterval(timerRef.current);
  }, [handleFinish, handleStop, serverIP, website]);

  if (error) {
    return (
      <Container className="py-5">
        <Alert variant="danger">{error}</Alert>
        <Button variant="secondary" onClick={() => navigate('/dashboard', { replace: true })}>Back to Dashboard</Button>
      </Container>
    );
  }

  if (!website) return <Container className="py-5"><Spinner animation="border" /></Container>;

  return (
    <Container className="py-5">
      {
        // Website2 is disabled — only show Website1 or jump to Post-Experiment
        order_index === 1 ? (
          <ProgressTracker currentStep="website1" />
        ) : (
          <ProgressTracker currentStep="post_experiment" />
        )
      }
      <Card className="shadow-lg border-0 mb-4">
        <Card.Body>
          <h2 className="mb-3">🔎 {website.website_name}</h2>
          <p className="text-muted">{website.description}</p>

          {serverIP ? (
            <>
              <Alert variant="success">
                ✅ Server started! Access it at: <strong>http://{serverIP}</strong>
              </Alert>
              <div className="d-flex gap-2 mb-3">
                <Button variant="warning" onClick={handleRestart} disabled={loading}>🔄 {loading ? 'Restarting...' : 'Restart'}</Button>
                <Button variant="danger" onClick={handleStop} disabled={loading}>🛑 {loading ? 'Stopping...' : 'Stop'}</Button>
              </div>
            </>
          ) : (
            <div className="d-grid mb-3">
              <Button variant="primary" onClick={handleStartServer} disabled={loading}>
                {loading ? 'Starting Server...' : '🚀 Start Pentest Server'}
              </Button>
            </div>
          )}
        </Card.Body>
      </Card>
      
      {timeLeft !== null && (
        <>
          <Alert variant={timeLeft > 300 ? "info" : "danger"}>
            ⏱ <strong>Time Remaining:</strong> {Math.floor(timeLeft / 60)}:{String(timeLeft % 60).padStart(2, '0')} minutes
          </Alert>
          {!startAdditionalTime && (
            <Alert variant="warning">
              ⏰ <strong>Additional Time Available:</strong> {ADDITIONAL_MINS}:00 minutes
            </Alert>
          )}
        </>
      )}
      
      <Card className="mb-4 shadow-sm">
        <Card.Body>
          <h5 className="mb-3">📄 Project Documents</h5>
          <div className="row">
          {designDocUrl && (
              <div className={dfdUrl ? "col-md-6" : "col-md-12"}>
                <Button href={`/document-view/${website.website_id}`} target="_blank" rel="noopener noreferrer" variant="primary" className="w-100 mb-2 mb-md-0">
                  <FontAwesomeIcon icon={faFilePdf} /> Open Design Document Viewer
                </Button>
              </div>
          )}
          {dfdUrl && (
              <div className="col-md-6">
                  <Button href={dfdUrl} target="_blank" rel="noopener noreferrer" variant="success" className="w-100">
                    <FontAwesomeIcon icon={faProjectDiagram} /> View Threat Model
                  </Button>
              </div>
          )}
          </div>

        </Card.Body>
      </Card>

      <Card className="shadow-sm">
        <Card.Body>
          <h4 className="mb-3">📝 Submit Findings</h4>
          {vulnForms.map((finding, index) => (
            <Card key={index} className="mb-3 shadow-sm">
              <Card.Header
                className="d-flex justify-content-between align-items-center"
                onClick={() => toggleCollapse(index)}
                style={{ cursor: 'pointer' }}
              >
                <strong>{finding.name || (Array.isArray(finding.vulnerability_type) && finding.vulnerability_type.join(', ')) || `Finding #${index + 1}`}</strong>
                <Button size="sm" variant="light">
                  {finding.collapsed ? "▶" : "▼"}
                </Button>
              </Card.Header>

              <Collapse in={!finding.collapsed}>
                <div>
                  <Card.Body>
                    {/* Place your existing <Form> content here */}
                    <Form key={index} className="border rounded p-3 mb-3">
                      <Alert variant="danger" className="py-2 mb-3">
                        <small>Fields marked with <i className="text-danger">*</i> are mandatory. All other fields are optional.</small>
                      </Alert>
                      <Row>
                        <Col md={6}>
                          <Form.Group className="mb-2">
                            <Form.Label>Vulnerability Type <i className="text-danger">*</i></Form.Label>
                            <div>
                              {vulnTypes.map(vt => (
                                <Badge
                                  key={vt}
                                  pill
                                  bg={finding.vulnerability_type?.includes(vt) ? 'primary' : 'secondary'}
                                  onClick={() => handleVulnTypeToggle(index, vt)}
                                  style={{ cursor: 'pointer', marginRight: '5px', marginBottom: '5px' }}
                                  className="p-2"
                                >
                                  {vt}
                                </Badge>
                              ))}
                            </div>
                          </Form.Group>
                        </Col>
                        <Col md={6}>
                          <Form.Group className="mb-2">
                            <Form.Label>Vulnerability Name <i className="text-danger">*</i></Form.Label>
                            <Form.Control
                              type="text"
                              value={finding.name}
                              onChange={(e) => handleFindingChange(index, 'name', e.target.value)}
                              required
                            />
                          </Form.Group>
                        </Col>
                      </Row>
                      <Form.Group className="mb-2">
                        <Form.Label>Affected URLs <i className="text-danger">*</i></Form.Label>
                        <Form.Control
                          type="text"
                          value={finding.affected_urls}
                          onChange={(e) => handleFindingChange(index, 'affected_urls', e.target.value)}
                          required
                        />
                      </Form.Group>
                      <Form.Group className="mb-2">
                        <Form.Label>Description</Form.Label>
                        <Form.Control
                          as="textarea"
                          rows={2}
                          value={finding.description}
                          onChange={(e) => handleFindingChange(index, 'description', e.target.value)}
                        />
                      </Form.Group>
                      <Form.Group className="mb-2">
                        <Form.Label>Payload</Form.Label>
                        <Form.Control
                          as="textarea"
                          rows={4}
                          value={finding.payload}
                          onChange={(e) => handleFindingChange(index, 'payload', e.target.value)}
                        />
                      </Form.Group>
                      <Form.Group className="mb-2">
                        <Form.Label>PoC Screenshot (Max. 5MB)</Form.Label>
                        {finding.poc_image_path && (
                          <div className="mb-2">
                            <img src={finding.poc_image_path} alt="preview" className="img-thumbnail" style={{maxWidth:160}} />
                          </div>
                        )}
                        <Form.Control
                          type="file"
                          accept="image/*"
                          disabled={finding.uploading}
                          onChange={e=>handleFileChange(index, e.target.files?.[0])}
                        />
                        {finding.uploading && <ProgressBar now={100} animated striped className="mt-2" />}
                      </Form.Group>
                      {dfdUrl && (
                        <>
                          <Form.Check
                            type="checkbox"
                            label="Used Threat Model?"
                            className="mb-2"
                            checked={finding.used_threat_model}
                            onChange={(e) => handleFindingChange(index, 'used_threat_model', e.target.checked)}
                          />
                          {finding.used_threat_model && (
                            <Form.Group className="mb-2">
                              <Form.Label>Threat Model Support Info</Form.Label>
                              <Form.Control
                                type="text"
                                value={finding.threat_model_info}
                                onChange={(e) => handleFindingChange(index, 'threat_model_info', e.target.value)}
                              />
                            </Form.Group>
                          )}
                        </>
                      )}
                      <Form.Check
                        type="checkbox"
                        label="Used Design Document?"
                        className="mb-2"
                        checked={finding.used_design_document}
                        onChange={(e) => handleFindingChange(index, 'used_design_document', e.target.checked)}
                      />
                      {finding.used_design_document && (
                        <Form.Group className="mb-2">
                          <Form.Label>Design Document Support Info</Form.Label>
                          <Form.Control
                            type="text"
                            value={finding.design_document_info}
                            onChange={(e) => handleFindingChange(index, 'design_document_info', e.target.value)}
                          />
                        </Form.Group>
                      )}
                      <div className="d-flex justify-content-between mt-3">
                        <Button variant="danger" onClick={()=>handleRemoveFinding(index)}>❌ Remove</Button>
                        <Button
                          variant="success"
                          disabled={finding.uploading}
                          onClick={()=>handleSaveFinding(index)}
                        >
                          {finding.isNew ? "💾 Add" : "💾 Save"}
                          </Button>
                      </div>
                    </Form>
                  </Card.Body>
                </div>
              </Collapse>
            </Card>
            
          ))}
          {vulnForms.length < 20 && (
            <div className="d-grid">
              <Button variant="outline-primary" onClick={handleAddFinding}>➕ Add New Finding</Button>
            </div>
          )}
        </Card.Body>
      </Card>
      <div className="d-flex gap-2 my-4">
        <Button
          variant="outline-primary"
          disabled={loading || completing || savingAll}
          onClick={async () => {
            setSavingAll(true);
            try {


              await saveAllFindings();        // bulk save
              // optional toast/snackbar here
            } catch (err) {
              console.error(err);
              alert(err.message ?? 'Bulk save failed.');
            } finally {
              setSavingAll(false);
            }
          }}
        >
          {savingAll ? (
            <>
              <Spinner animation="border" size="sm" className="me-2" />
              Saving…
            </>
          ) : (
            '💾 Save All Findings'
          )}
        </Button>
        <Button
          variant="success"
          disabled={loading || completing}
          onClick={handleFinish}
        >
          {completing ? (
            <>
              <Spinner animation="border" size="sm" className="me-2" />
              Completing…
            </>
          ) : (
            '✅ Complete this Website'
          )}
        </Button>

        </div>

    </Container>
  );
}

export default WebsitePage;
